import { type MessageDescriptor } from 'react-intl';

import { engineeringMessages as messages } from './jsm-playground-engineering-messages';

export type EngineeringWorkflowStatusId =
	| 'toDo'
	| 'triaged'
	| 'inProgress'
	| 'pendingOnReporter'
	| 'addedToBacklog'
	| 'completed'
	| 'cancelled';

export type EngineeringWorkflowStatus = {
	id: EngineeringWorkflowStatusId;
	label: MessageDescriptor;
	tone: 'neutral' | 'blue' | 'green' | 'red';
};

export type EngineeringRequestType = 'bug' | 'feature' | 'support' | 'system';

export type EngineeringPriority = 'Highest' | 'High' | 'Medium' | 'Low' | 'Lowest';

export type EngineeringQueueRow = {
	assignee?: string;
	created: number;
	descriptionTail: MessageDescriptor;
	priority: EngineeringPriority;
	reporter: string;
	requestType: EngineeringRequestType;
	sla: MessageDescriptor;
	slaTone: 'breached' | 'paused' | 'running' | 'met';
	status: EngineeringWorkflowStatus;
	summary: MessageDescriptor;
};

export const ENGINEERING_WORKFLOW = [
	{ id: 'toDo', label: messages.statusToDo, tone: 'neutral' },
	{ id: 'triaged', label: messages.statusTriaged, tone: 'blue' },
	{ id: 'inProgress', label: messages.statusInProgress, tone: 'blue' },
	{ id: 'pendingOnReporter', label: messages.statusPendingOnReporter, tone: 'blue' },
	{ id: 'addedToBacklog', label: messages.addedToBacklog, tone: 'blue' },
	{ id: 'completed', label: messages.statusCompleted, tone: 'green' },
	{ id: 'cancelled', label: messages.statusCancelled, tone: 'neutral' },
] as const satisfies readonly EngineeringWorkflowStatus[];

export const ENGINEERING_QUEUE_ROWS: EngineeringQueueRow[] = [
	{
		assignee: 'Issac Varghese',
		created: Date.UTC(2024, 5, 24),
		descriptionTail: messages.descriptionTailMoreDetails,
		priority: 'Highest',
		reporter: 'Priya Hansra',
		requestType: 'bug',
		sla: messages.slaToday1130Am,
		slaTone: 'breached',
		status: ENGINEERING_WORKFLOW[2],
		summary: messages.summarySaveButton,
	},
	{
		assignee: 'Issac Varghese',
		created: Date.UTC(2024, 5, 24),
		descriptionTail: messages.descriptionTailEndOfWeek,
		priority: 'Medium',
		reporter: 'Robi Lopez',
		requestType: 'feature',
		sla: messages.slaToday1130Pm,
		slaTone: 'running',
		status: ENGINEERING_WORKFLOW[4],
		summary: messages.summaryExportCsv,
	},
	{
		assignee: 'Jordan Ellis',
		created: Date.UTC(2024, 5, 3),
		descriptionTail: messages.descriptionTailBlocking,
		priority: 'Highest',
		reporter: 'Raul Gonzalez',
		requestType: 'system',
		sla: messages.slaToday1130Pm,
		slaTone: 'paused',
		status: ENGINEERING_WORKFLOW[4],
		summary: messages.summaryLoginTimeout,
	},
	{
		assignee: 'Jordan Ellis',
		created: Date.UTC(2024, 5, 24),
		descriptionTail: messages.descriptionTailEndOfWeek,
		priority: 'Lowest',
		reporter: 'Raul Gonzalez',
		requestType: 'feature',
		sla: messages.sla25Jun1130Pm,
		slaTone: 'running',
		status: ENGINEERING_WORKFLOW[2],
		summary: messages.summaryDarkMode,
	},
	{
		created: Date.UTC(2024, 5, 3),
		descriptionTail: messages.descriptionTailBlocking,
		priority: 'Medium',
		reporter: 'Robi Lopez',
		requestType: 'support',
		sla: messages.slaTomorrow500Pm,
		slaTone: 'running',
		status: ENGINEERING_WORKFLOW[2],
		summary: messages.summaryApiAuthError,
	},
	{
		assignee: 'Laila Ali',
		created: Date.UTC(2024, 5, 11),
		descriptionTail: messages.descriptionTailBlocking,
		priority: 'Medium',
		reporter: 'Raul Gonzalez',
		requestType: 'support',
		sla: messages.slaTomorrow500Pm,
		slaTone: 'running',
		status: ENGINEERING_WORKFLOW[2],
		summary: messages.summaryConfigureSso,
	},
	{
		assignee: 'Laila Ali',
		created: Date.UTC(2024, 5, 18),
		descriptionTail: messages.descriptionTailBlocking,
		priority: 'Medium',
		reporter: 'Robi Lopez',
		requestType: 'support',
		sla: messages.slaTomorrow1130Am,
		slaTone: 'running',
		status: ENGINEERING_WORKFLOW[1],
		summary: messages.summaryApiAuthError,
	},
	{
		assignee: 'Kayla Parajuli',
		created: Date.UTC(2024, 5, 1),
		descriptionTail: messages.descriptionTailEndOfWeek,
		priority: 'High',
		reporter: 'Robi Lopez',
		requestType: 'feature',
		sla: messages.slaToday400Pm,
		slaTone: 'paused',
		status: ENGINEERING_WORKFLOW[3],
		summary: messages.summaryExportCsv,
	},
	{
		created: Date.UTC(2024, 5, 11),
		descriptionTail: messages.descriptionTailEndOfWeek,
		priority: 'Lowest',
		reporter: 'Priya Hansra',
		requestType: 'support',
		sla: messages.slaToday1130Pm,
		slaTone: 'running',
		status: ENGINEERING_WORKFLOW[0],
		summary: messages.summaryCrmSync,
	},
];

export const ENGINEERING_REQUEST_TYPE_LABELS: Record<EngineeringRequestType, MessageDescriptor> = {
	bug: messages.requestTypeBug,
	feature: messages.requestTypeFeature,
	support: messages.requestTypeSupport,
	system: messages.requestTypeSystem,
};

export const ENGINEERING_PRIORITY_LABELS: Record<EngineeringPriority, MessageDescriptor> = {
	Highest: messages.priorityHighest,
	High: messages.priorityHigh,
	Medium: messages.priorityMedium,
	Low: messages.priorityLow,
	Lowest: messages.priorityLowest,
};
